import React, { useEffect, useMemo, useState } from 'react';
import {
  X, CalendarClock, FileText, CalendarRange, Users, CalendarDays, ListChecks,
  CheckCircle2, Circle, Clock, Layers3, MessageSquare, ShieldCheck, Ban, GitBranch,
} from 'lucide-react';
import studyBuilderService from '../../services/studyBuilderService';
import { getStudyDraftReviewContext } from '../../services/reviewWorkflowApi';
import ScheduleReviewCard, { SectionLabel, Chip, Muted } from './ScheduleReviewCard';
import SchedulePreviewModal from './SchedulePreviewModal';

/**
 * Full design detail for one study draft, opened from the Study Drafts list.
 *
 * Replaces the old in-row dropdown, which predated FECOA-1236 and only showed
 * the legacy assessment-window shape (raw day offsets + UTC start/end times) —
 * intra-day timepoints and their per-timepoint surveys were invisible. The
 * modal loads its own survey/reminder/review context and renders every
 * schedule through the shared ScheduleReviewCard, so it always matches the
 * full-page Study Design Review.
 */

const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', Icon: Clock },
  in_review: { label: 'Pending Review', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200', Icon: CheckCircle2 },
  denied: { label: 'Needs Revision', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200', Icon: MessageSquare },
  published: { label: 'Published', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', Icon: CheckCircle2 },
  superseded: { label: 'Superseded', cls: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400', Icon: Layers3 },
};

const fmtDateTime = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return v; }
};

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="bg-gray-50 dark:bg-gray-900/60 rounded-xl border border-gray-100 dark:border-gray-700 p-3 flex items-center gap-3">
    <div className="bg-primary-50 dark:bg-primary-900/30 p-2 rounded-lg flex-shrink-0">
      <Icon className="w-4 h-4 text-primary-600 dark:text-primary-300" />
    </div>
    <div className="min-w-0">
      <div className="text-base font-bold text-gray-900 dark:text-gray-100 leading-tight truncate">{value}</div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  </div>
);

const StudyDraftDetailModal = ({ open, draft, chain = [], onClose }) => {
  const [surveys, setSurveys] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reviewCtx, setReviewCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let active = true;
    if (!open || !draft?.id) return undefined;
    setLoading(true);
    Promise.all([
      studyBuilderService.getSurveysByStudyName(draft.name).catch(() => null),
      studyBuilderService.getReminderTemplates(draft.id).catch(() => null),
      getStudyDraftReviewContext(draft.id).catch(() => null),
    ])
      .then(async ([surveyData, templateData, ctx]) => {
        if (!active) return;
        const loaded = surveyData?.surveys || [];
        setReminders(templateData?.results || templateData || []);
        setReviewCtx(ctx);
        // The study-name lookup misses surveys when the draft name has drifted
        // from the Study title — resolve any still-unnamed ids individually,
        // inside the loading window so a bare "Survey #id" never flashes.
        const draftSchedules = Array.isArray(draft.data?.schedules) ? draft.data.schedules : [];
        const extras = await studyBuilderService
          .resolveMissingScheduleSurveys(draftSchedules, loaded)
          .catch(() => []);
        if (!active) return;
        setSurveys(extras.length > 0 ? [...loaded, ...extras] : loaded);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, draft?.id, draft?.name, draft?.review_status]);

  // Escape closes the detail modal, unless the calendar visualizer is stacked
  // on top — then Escape belongs to it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !previewOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, previewOpen]);

  const surveyName = useMemo(() => (id) => {
    const found = surveys.find((s) => s.id === parseInt(id, 10));
    return found ? found.name : `Survey #${id}`;
  }, [surveys]);
  const reminderName = useMemo(() => (id) => {
    const found = reminders.find((r) => r.id === parseInt(id, 10));
    return found ? found.name : `Reminder #${id}`;
  }, [reminders]);

  if (!open || !draft) return null;

  const data = draft.data || {};
  const arms = Array.isArray(data.arms) ? data.arms : [];
  const schedules = Array.isArray(data.schedules) ? data.schedules : [];
  const startDay = schedules[0]?.studyStartDay;
  const status = STATUS_META[draft.review_status] || STATUS_META.draft;
  const StatusIcon = status.Icon;
  const rs = draft.review_status || 'draft';

  const readiness = [
    { label: 'Study start day set', done: startDay !== undefined },
    { label: 'Arms configured', done: arms.length > 0 },
    { label: 'Schedules configured', done: schedules.length > 0 },
    { label: 'Surveys assigned', done: schedules.some((s) => Array.isArray(s?.standardSurveys) && s.standardSurveys.length > 0) },
  ];
  const doneCount = readiness.filter((r) => r.done).length;

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-50 dark:bg-gray-900 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Design details for ${draft.name}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Study Design · read-only</p>
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">{draft.name}</h2>
              {chain.length > 1 && (
                <span className="flex-shrink-0 px-1.5 py-0.5 text-[11px] font-semibold rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                  v{draft.version_number ?? 1}
                </span>
              )}
            </div>
            {draft.submitted_at && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {rs === 'in_review' ? 'Submitted' : 'Last submitted'} {fmtDateTime(draft.submitted_at)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${status.cls}`}>
              <StatusIcon className="w-3.5 h-3.5" /> {status.label}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent mb-3" />
              <p className="text-gray-500 dark:text-gray-400 text-sm">Loading design details...</p>
            </div>
          ) : (
            <>
              {/* Review notes by state (FECOA-1603) */}
              {rs === 'in_review' && reviewCtx?.submission_note && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
                  <SectionLabel>Submitter&apos;s Note</SectionLabel>
                  <div className="flex gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700 dark:text-gray-200 italic">&ldquo;{reviewCtx.submission_note}&rdquo;</p>
                  </div>
                </div>
              )}
              {rs === 'approved' && reviewCtx?.approval && (
                <div className="bg-teal-50/60 dark:bg-teal-900/15 rounded-xl border border-teal-200 dark:border-teal-800/50 p-4 text-sm">
                  <div className="flex items-center gap-1.5 text-teal-700 dark:text-teal-300 font-medium">
                    <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
                    Approved by {reviewCtx.approval.decided_by_name}
                    {reviewCtx.approval.decided_at && (
                      <span className="text-teal-500/80 dark:text-teal-300/70 font-normal">· {fmtDateTime(reviewCtx.approval.decided_at)}</span>
                    )}
                  </div>
                  {reviewCtx.approval.message && (
                    <p className="mt-0.5 ml-5 text-teal-700/90 dark:text-teal-200/90 italic">&ldquo;{reviewCtx.approval.message}&rdquo;</p>
                  )}
                </div>
              )}
              {rs === 'denied' && reviewCtx?.denial_history?.length > 0 && (
                <div className="bg-red-50/60 dark:bg-red-900/15 rounded-xl border border-red-200 dark:border-red-800/50 p-4 text-sm">
                  <div className="flex items-center gap-1.5 text-red-700 dark:text-red-300 font-medium">
                    <Ban className="w-3.5 h-3.5 flex-shrink-0" />
                    Denied by {reviewCtx.denial_history[0].decided_by_name}
                    {reviewCtx.denial_history[0].decided_at && (
                      <span className="text-red-500/80 dark:text-red-300/70 font-normal">· {fmtDateTime(reviewCtx.denial_history[0].decided_at)}</span>
                    )}
                  </div>
                  {reviewCtx.denial_history[0].reason && (
                    <p className="mt-0.5 ml-5 text-red-700/90 dark:text-red-200/90 italic">&ldquo;{reviewCtx.denial_history[0].reason}&rdquo;</p>
                  )}
                </div>
              )}

              {/* Version chain (FECOA-1831): superseded versions are hidden from
                  the drafts table, so this is where the full amendment history
                  of a design stays visible. */}
              {chain.length > 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <GitBranch className="w-4 h-4 text-primary-600 dark:text-primary-300" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Version History</h3>
                  </div>
                  <div className="space-y-1.5">
                    {chain.map((v) => {
                      const vMeta = STATUS_META[v.review_status] || STATUS_META.draft;
                      const isCurrent = v.id === draft.id;
                      return (
                        <div
                          key={v.id}
                          className={`flex items-center gap-3 text-sm rounded-lg px-2 py-1.5 ${isCurrent ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''}`}
                        >
                          <span className="w-8 flex-shrink-0 font-semibold text-gray-700 dark:text-gray-200">v{v.version_number ?? 1}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${vMeta.cls}`}>{vMeta.label}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {v.review_status === 'published' && v.published_at
                              ? `Published ${fmtDateTime(v.published_at)}`
                              : `Updated ${fmtDateTime(v.updated_at)}`}
                          </span>
                          {isCurrent && (
                            <span className="ml-auto text-[11px] font-medium text-primary-600 dark:text-primary-300 flex-shrink-0">viewing</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stat tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatTile icon={CalendarRange} label="Study start" value={startDay !== undefined ? `Day ${startDay}` : '—'} />
                <StatTile icon={Users} label={arms.length === 1 ? 'Arm' : 'Arms'} value={arms.length} />
                <StatTile icon={CalendarClock} label={schedules.length === 1 ? 'Schedule' : 'Schedules'} value={schedules.length} />
                <StatTile icon={FileText} label={surveys.length === 1 ? 'Survey' : 'Surveys'} value={surveys.length} />
              </div>

              {/* Readiness */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-primary-600 dark:text-primary-300" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Readiness</h3>
                  </div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{doneCount}/{readiness.length} complete</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {readiness.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {r.done
                        ? <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" />
                        : <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />}
                      <span className={r.done ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}>{r.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arms */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers3 className="w-4 h-4 text-primary-600 dark:text-primary-300" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Arms</h3>
                </div>
                {arms.length > 0
                  ? <div className="flex flex-wrap gap-2">{arms.map((a, i) => <Chip key={i}>{a}</Chip>)}</div>
                  : <Muted>No arms configured</Muted>}
              </div>

              {/* Schedules */}
              <div>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <CalendarClock className="w-4 h-4 text-primary-600 dark:text-primary-300" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Schedules</h3>
                  <span className="text-xs text-gray-400">({schedules.length})</span>
                  {schedules.length > 0 && (
                    <button
                      onClick={() => setPreviewOpen(true)}
                      className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-200 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-lg transition-colors"
                      title="Visualize this schedule against a sample enrollment date"
                    >
                      <CalendarDays className="w-4 h-4" />
                      <span>Preview calendar</span>
                    </button>
                  )}
                </div>
                {schedules.length > 0 ? (
                  <div className="space-y-4">
                    {schedules.map((s, i) => (
                      <ScheduleReviewCard key={i} schedule={s} surveyName={surveyName} reminderName={reminderName} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
                    <Muted>No schedules configured</Muted>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>

    {/* Sibling of the backdrop (not a child) so clicks inside the visualizer
        don't bubble to the backdrop's onClick and close both modals. */}
    <SchedulePreviewModal
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
      schedules={schedules}
      studyStartDay={startDay}
    />
    </>
  );
};

export default StudyDraftDetailModal;
